import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, Input, Table, PopConfirm, TreeSelect } from '@maxtropy/components';
import { TreeSelectProps } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { apiV2EnergyAnalysisGroupAndUnitPost } from '@maxtropy/device-customer-apis-v2';

export interface DeptAreaListItem {
  id: string;
  name: string;
  isNew?: boolean; // 是否为新增（新增时不传 deptAreaId）
  configs: {
    type: number;
    energyUnitGroupId?: number;
    energyUnitId?: number;
    energyUnitName?: string;
    mediumId?: number;
    coefficient?: number;
  }[];
}

interface Props {
  type: 'dept' | 'area';
  isEdit: boolean;
  form: any;
  configList: Array<{
    type: number;
    label: string;
    unit: string;
  }>;
}

const DeptAreaList: React.FC<Props> = ({ type, isEdit, form, configList }) => {
  const [treeNodeData, setTreeNodeData] = useState<TreeSelectProps['treeData']>([]);
  const [nodeMap, setNodeMap] = useState<
    Record<string | number, { energyUnitId?: number; energyUnitGroupId?: number }>
  >({});

  const listName = type === 'dept' ? 'deptList' : 'areaList';

  // 过滤掉光伏(type=1)和中水(type=3)
  const filteredConfigList = configList.filter(config => config.type !== 1 && config.type !== 3);

  const formatToNodeData = useCallback(
    (
      data: any[],
      map: Record<string | number, { energyUnitId?: number; energyUnitGroupId?: number }> = {},
      level: number = 0
    ): TreeSelectProps['treeData'] => {
      return data.map(i => {
        const children = i.children ?? i.energyUnits;
        const hasChildren = children && children.length > 0;
        const nodeValue = i.energyUnitId ?? i.id;
        // 存到 map
        map[nodeValue] = {
          energyUnitId: i.energyUnitId,
          energyUnitGroupId: i.energyUnitGroupId,
        };

        return {
          title: i.name,
          value: nodeValue,
          energyUnitId: i.energyUnitId,
          energyUnitGroupId: i.energyUnitGroupId,
          children: hasChildren ? formatToNodeData(children, map, level + 1) : [],
          selectable: level !== 0,
        };
      });
    },
    []
  );

  // 获取用能分析组和用能单元树形数据
  useEffect(() => {
    apiV2EnergyAnalysisGroupAndUnitPost({}).then(res => {
      const map: Record<string | number, { energyUnitId?: number; energyUnitGroupId?: number }> = {};
      const tree = formatToNodeData(res.list || [], map);
      setTreeNodeData(tree);
      setNodeMap(map);
    });
  }, [formatToNodeData]);

  const handleEnergyUnitChange = (rowIndex: number, configIndex: number, value: number) => {
    const currentList = form.getFieldValue(listName) || [];
    const energyUnitGroupId = nodeMap[value]?.energyUnitGroupId;
    const newList = [...currentList];
    newList[rowIndex].configs[configIndex] = {
      ...newList[rowIndex].configs[configIndex],
      energyUnitGroupId,
      energyUnitId: value,
      energyUnitName: undefined,
    };
    form.setFieldValue(listName, newList);
  };

  return (
    <Form.List name={listName}>
      {(fields, { add, remove }) => {
        const handleAddRow = () => {
          add({
            id: `${Date.now()}`,
            name: '',
            isNew: true, // 标记为新增
            configs: filteredConfigList.map(config => ({
              type: config.type,
              energyUnitGroupId: undefined,
              energyUnitId: undefined,
              energyUnitName: undefined,
              mediumId: undefined,
              coefficient: 1,
            })),
          });
        };

        const handleDeleteRow = (index: number) => {
          remove(index);
        };

        const columns = [
          {
            title: `${type === 'dept' ? '部门' : '区域'}名称`,
            dataIndex: 'name',
            width: 200,
            render: (_: any, field: any, index: number) => (
              <Form.Item
                name={[field.name, 'name']}
                style={{ marginBottom: 0 }}
                rules={[
                  { required: true, message: '请输入名称' },
                  { max: 50, message: '最多输入50个字符' },
                ]}
              >
                <Input disabled={!isEdit} placeholder="请输入" maxLength={50} />
              </Form.Item>
            ),
          },
          ...filteredConfigList.map((config, configIndex) => ({
            title: config.label,
            dataIndex: `config_${config.type}`,
            width: 260,
            render: (_: any, field: any, index: number) => (
              <Form.Item name={[field.name, 'configs', configIndex, 'energyUnitId']} style={{ marginBottom: 0 }}>
                <TreeSelect
                  disabled={!isEdit}
                  treeData={treeNodeData}
                  style={{ width: '100%' }}
                  allowClear
                  showSearch
                  treeNodeFilterProp="title"
                  treeDefaultExpandAll
                  placeholder="请选择"
                  onChange={value => handleEnergyUnitChange(field.name, configIndex, value)}
                />
              </Form.Item>
            ),
          })),
          {
            title: '操作',
            width: 100,
            fixed: 'right' as const,
            render: (_: any, field: any, index: number) => (
              <PopConfirm title="确定删除吗？" onConfirm={() => handleDeleteRow(field.name)}>
                <Button disabled={!isEdit} type="link">
                  删除
                </Button>
              </PopConfirm>
            ),
          },
        ];

        return (
          <>
            <div style={{ marginBottom: 8, display: 'inline-block' }}>
              <Button type="primary" disabled={!isEdit} icon={<PlusOutlined />} onClick={handleAddRow}>
                新建
              </Button>
            </div>
            <Table
              rowKey="key"
              dataSource={fields}
              columns={columns}
              pagination={false}
              scroll={{ x: 'max-content' }}
            />
          </>
        );
      }}
    </Form.List>
  );
};

export default DeptAreaList;
